import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "./redux";

interface UseSurveySchedulerProps {
  engagementTrackerProvider: IEngagementTrackerProvider;
}

/**
 * Survey Scheduler Hook
 *
 * New behavior (engagement-based):
 * - Triggers surveys ONLY after meaningful engagement
 * - Respects 7-day minimum wait, 30-day frequency, 14-day snooze
 * - Triggers after positive events (PDF downloads, workflow completions)
 * - Never triggers on first visit or routine page views
 * - Uses 10-15% sampling rate
 */
export const useSurveyScheduler = ({
  engagementTrackerProvider,
}: UseSurveySchedulerProps) => {
  const location = useLocation();
  const user = useAppSelector((state) => state.auth.user);
  const clinicList = useAppSelector((state) => state.clinic?.clinicList || []);
  const lastCheckRef = useRef<number>(0);
  const intervalRef = useRef<number>();

  /**
   * Try to trigger survey with full eligibility checks
   */
  const tryTriggerSurvey = () => {
    if (!user) {
      return;
    }

    const now = Date.now();
    const timeSinceLastCheck = now - lastCheckRef.current;

    // Light throttle to prevent rapid-fire checks (30 seconds)
    const throttleTime = 30 * 1000; // 30 seconds
    if (timeSinceLastCheck < throttleTime) {
      return;
    }

    lastCheckRef.current = now;

    // Check eligibility using engagement tracker
    const eligibility = engagementTrackerProvider.isEligibleForSurvey();

    // Only trigger if user is eligible
    if (!eligibility.eligible) {
      return;
    }

    // Additional validation: must have clinics and profile
    const hasAssignedClinics =
      Array.isArray(clinicList) && clinicList.length > 0;
    const hasUserProfile = user?.firstName && user?.email;

    if (!hasAssignedClinics || !hasUserProfile) {
      // Skip survey if essential user data is missing
      // The StarRatingPrompt component will perform final validation
      return;
    }

    // Trigger the survey
    window.dispatchEvent(new Event("openSurvey"));
  };

  /**
   * Listen for positive events that should trigger survey check
   */
  useEffect(() => {
    const onPositiveEvent = () => {
      tryTriggerSurvey();
    };

    const onWorkflowCompletion = () => {
      tryTriggerSurvey();
    };

    const onMilestoneAchieved = () => {
      tryTriggerSurvey();
    };

    // Listen for custom events that indicate good times to show survey
    window.addEventListener("positiveEvent", onPositiveEvent);
    window.addEventListener("workflowCompleted", onWorkflowCompletion);
    window.addEventListener("milestoneAchieved", onMilestoneAchieved);

    return () => {
      window.removeEventListener("positiveEvent", onPositiveEvent);
      window.removeEventListener("workflowCompleted", onWorkflowCompletion);
      window.removeEventListener("milestoneAchieved", onMilestoneAchieved);
    };
  }, [user, clinicList]);

  /**
   * Check on route changes (secondary trigger after workflows)
   * This ensures survey can show during normal navigation for eligible users
   */
  useEffect(() => {
    if (location.pathname && lastCheckRef.current > 0 && user) {
      // Check on 30% of route changes to catch eligible users during navigation
      // Combined with 30-day frequency + engagement criteria, this is still non-intrusive
      const shouldCheckOnRouteChange = Math.random() < 0.3; // 30% chance
      if (shouldCheckOnRouteChange) {
        tryTriggerSurvey();
      }
    } else if (location.pathname) {
      // Initialize the ref on first load
      lastCheckRef.current = Date.now();
    }
  }, [location.pathname, user]);

  /**
   * Periodic check every 2 hours for long-session users
   * Catches eligible users who are actively using the app but haven't triggered workflows recently
   */
  useEffect(() => {
    if (user) {
      intervalRef.current = window.setInterval(() => {
        tryTriggerSurvey();
      }, 2 * 60 * 60 * 1000); // Check every 2 hours
    }

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, [user]);
};
